import type {
  ExtractResource,
  Reference,
  ResourceType,
} from '@medplum/fhirtypes'
import type { useEditor } from '@tiptap/react'
import { type JSX, useEffect, useRef, useState } from 'react'
import { HoverButton } from '#src/components/button/HoverButton.tsx'
import { MedplumAppLink } from '#src/components/medplum/MedplumAppLink.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'

import { referenceDisplay } from '@seen/core/display'
interface FhirReferenceProps<T extends ResourceType> {
  value: Reference<ExtractResource<T>> | undefined
  onChange: (reference: Reference<ExtractResource<T>> | undefined) => void
  disabled?: boolean
}

export function FhirReference<T extends ResourceType>({
  value,
  onChange,
  disabled,
}: FhirReferenceProps<T>): JSX.Element {
  const [isEditing, setIsEditing] = useState(false)
  const editorRef = useRef<ReturnType<typeof useEditor>>(null)

  // Control focus to prevent unwanted scrolling using autofocus='end'
  useEffect(() => {
    if (isEditing && editorRef.current) {
      editorRef.current.commands.focus()
    }
  }, [isEditing])

  return (
    <div className="group flex items-center gap-2">
      {isEditing ? (
        <RichTextEditor
          editorRef={editorRef}
          variant="unstyled"
          // The edited text is saved as the Reference's display, so editing
          // starts from the stored display.
          // lint-reference-display-ignore -- edited and written to FHIR
          value={value?.display ?? value?.reference}
          onChange={(newDisplay) =>
            onChange({
              ...value,
              display: newDisplay,
            })
          }
          onBlur={() => setIsEditing(false)}
        />
      ) : (
        <>
          <ReferenceLink reference={value}>
            {referenceDisplay(value) ?? value?.reference}
          </ReferenceLink>
          {!disabled && (
            <>
              <HoverButton type="edit" onClick={() => setIsEditing(true)} />
              <HoverButton type="cancel" onClick={() => onChange(undefined)} />
            </>
          )}
        </>
      )}
    </div>
  )
}

function ReferenceLink<T extends ResourceType>(props: {
  children: React.ReactNode
  reference: Reference<ExtractResource<T>> | undefined
}): JSX.Element {
  return <MedplumAppLink to={props.reference}>{props.children}</MedplumAppLink>
}
